import type { IAction } from '@chainlit/react-client';

import {
  focusPageSection,
  isCurrentPageTarget,
  storePendingPageInteraction
} from './page-interaction';
import {
  type NavigationTarget,
  type PageSection,
  isNavigationTarget,
  isPageSection,
  pageTargetRegistry
} from './page-targets';

export const IPMA_NAVIGATION_ACTION = 'ipma_navigation';
export const navigationTargets = pageTargetRegistry;
export { isNavigationTarget, pageTargetRegistry };
export type { NavigationTarget, PageSection };

export type NavigationBehavior = 'auto' | 'button';

interface BaseNavigationAction {
  id: string;
  target: NavigationTarget;
  behavior: NavigationBehavior;
}

export interface NavigateAction extends BaseNavigationAction {
  type: 'navigate';
}

export interface NavigateAndFocusAction extends BaseNavigationAction {
  type: 'navigate_and_focus';
  section: PageSection;
}

export type NavigationAction = NavigateAction | NavigateAndFocusAction;

const IPMA_HOSTS = new Set(['ipma.world', 'www.ipma.world']);

export const getNavigationAction = (
  action: IAction
): NavigationAction | null => {
  if (
    action.name !== IPMA_NAVIGATION_ACTION ||
    typeof action.id !== 'string' ||
    !action.id
  ) {
    return null;
  }

  const { type, target, behavior, section } = action.payload || {};
  if (
    !isNavigationTarget(target) ||
    (behavior !== 'auto' && behavior !== 'button')
  ) {
    return null;
  }

  if (type === 'navigate') {
    return {
      id: action.id,
      type,
      target,
      behavior: action.isRestored ? 'button' : behavior
    };
  }

  if (type === 'navigate_and_focus' && isPageSection(target, section)) {
    return {
      id: action.id,
      type,
      target,
      section,
      behavior: action.isRestored ? 'button' : behavior
    };
  }

  return null;
};

export const isNavigationAction = (action: IAction): boolean =>
  getNavigationAction(action) !== null;

export const resolveNavigationUrl = (target: NavigationTarget): string => {
  const approvedDestination = new URL(pageTargetRegistry[target].url);

  // Keep same-site IPMA navigation on the current origin so the widget's
  // localStorage-backed thread id survives the page change.
  if (
    IPMA_HOSTS.has(window.location.hostname) &&
    IPMA_HOSTS.has(approvedDestination.hostname)
  ) {
    return `${window.location.origin}${approvedDestination.pathname}${approvedDestination.search}${approvedDestination.hash}`;
  }

  return approvedDestination.href;
};

export const navigateToIpmaTarget = (target: NavigationTarget): void => {
  window.location.assign(resolveNavigationUrl(target));
};

interface ExecuteNavigationOptions {
  currentUrl?: string;
  root?: Document;
  storage?: Storage;
  navigate?: (target: NavigationTarget) => void;
}

export const executeNavigationAction = async (
  action: NavigationAction,
  {
    currentUrl = window.location.href,
    root = document,
    storage = sessionStorage,
    navigate = navigateToIpmaTarget
  }: ExecuteNavigationOptions = {}
): Promise<boolean> => {
  if (action.type === 'navigate') {
    navigate(action.target);
    return true;
  }

  if (isCurrentPageTarget(action.target, currentUrl)) {
    return focusPageSection(action.target, action.section, root);
  }

  // Only semantic target/section keys cross the page boundary. The
  // destination page resolves both through the trusted registry.
  storePendingPageInteraction(action, storage);
  navigate(action.target);
  return true;
};
